/**
 * MfaGate — 2FA (TOTP) for the web app.
 *  - If the user already has a TOTP factor and the session is aal1 → asks for the 6-digit code.
 *  - If the user is super_admin and has no factor → forces enrollment (QR + code).
 *  - Everyone else without a factor passes through (they can enroll later).
 * Server side enforcement lives in server/middleware/auth.js (MFA_REQUIRED_SUPER_ADMIN).
 */
import React, { useEffect, useState } from "react";
import { Box, Button, Center, Heading, Image, Input, Spinner, Text, VStack } from "@chakra-ui/react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/AuthContext";

type Stage = "loading" | "ok" | "challenge" | "enroll";

const MfaGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin, signOut, profileLoading } = useAuth() as any;
  const [stage, setStage] = useState<Stage>("loading");
  const [factorId, setFactorId] = useState<string>("");
  const [qr, setQr] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (profileLoading) return; // wait until we know the role
    (async () => {
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (cancelled) return;
      if (aal?.currentLevel === "aal2") return setStage("ok");
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const verified = (factors?.totp || []).find((f: any) => f.status === "verified");
      if (verified) { setFactorId(verified.id); return setStage("challenge"); }
      if (!isSuperAdmin) return setStage("ok");
      // super_admin without 2FA: clean up half-finished factors, then enroll
      for (const f of (factors?.all || []).filter((f: any) => f.status !== "verified")) {
        await supabase.auth.mfa.unenroll({ factorId: f.id });
      }
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp", friendlyName: "LeadiumX" });
      if (error || !data) { setErr(error?.message || "No se pudo iniciar 2FA"); return setStage("enroll"); }
      setFactorId(data.id); setQr(data.totp.qr_code); setSecret(data.totp.secret);
      setStage("enroll");
    })();
    return () => { cancelled = true; };
  }, [isSuperAdmin, profileLoading]);

  const verify = async () => {
    setBusy(true); setErr("");
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
    setBusy(false);
    if (error) return setErr("Código incorrecto. Intenta de nuevo.");
    setStage("ok");
  };

  if (stage === "ok") return <>{children}</>;
  if (stage === "loading") return <Center minH="100vh"><Spinner size="xl" /></Center>;

  return (
    <Center minH="100vh" p={4}>
      <Box maxW="420px" w="100%" p={8} borderWidth="1px" borderRadius="lg">
        <VStack spacing={4} align="stretch">
          <Heading size="md">Verificación en dos pasos</Heading>
          {stage === "enroll" ? (
            <>
              <Text fontSize="sm">Tu cuenta de administrador necesita 2FA. Escanea este código con Google Authenticator, Authy o 1Password y escribe el código de 6 dígitos.</Text>
              {qr && <Center><Image src={qr} alt="QR 2FA" boxSize="200px" /></Center>}
              {secret && <Text fontSize="xs" wordBreak="break-all">Clave manual: {secret}</Text>}
            </>
          ) : (
            <Text fontSize="sm">Escribe el código de 6 dígitos de tu app de autenticación.</Text>
          )}
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456"
                 inputMode="numeric" maxLength={6} onKeyDown={(e) => e.key === "Enter" && verify()} />
          {err && <Text color="red.500" fontSize="sm">{err}</Text>}
          <Button colorScheme="blue" onClick={verify} isLoading={busy} isDisabled={code.trim().length !== 6}>Verificar</Button>
          <Button variant="ghost" size="sm" onClick={() => signOut && signOut()}>Cerrar sesión</Button>
        </VStack>
      </Box>
    </Center>
  );
};

export default MfaGate;
